// Pricebook, Supplier Quote, Xero Sync, Historical Pricing, and Price Suggestion APIs
import type { PaginatedResponse } from '@quotech/api'
import { getApiClient, withOrgConfig, asArray } from '../quote-api-helpers'
import type {
  PriceBook,
  PriceBookDetail,
  PriceBookItem,
  CreatePriceBookItemRequest,
  SupplierQuoteListItem,
  SupplierQuoteDetail,
  ExtractionResult,
  ExtractionStatus,
  ApplyLinesResult,
  XeroSyncStatus,
  XeroSyncResult,
  PriceSuggestion,
  HistoricalPricingAnalysis,
  GenerateHistoricalPricebookResult,
  HistoricalSuggestionsResult,
  ItemPriceHistoryResult,
  XeroContactsStatus,
  XeroContactsSyncResult,
} from '../quote-api-pricing-types'

export const pricingApi = {
  // Pricebook APIs

  async listPricebooks() {
    const client = getApiClient()
    const res = await client.get<PaginatedResponse<PriceBook> | PriceBook[]>(
      `/quotebuilder/pricebooks/`,
      withOrgConfig()
    )
    return asArray(res.data)
  },

  async getPricebook(id: string): Promise<PriceBookDetail> {
    const client = getApiClient()
    const res = await client.get<PriceBookDetail>(`/quotebuilder/pricebooks/${id}/`, withOrgConfig())
    return res.data
  },

  async listPricebookItems(pricebookId: string, params?: { search?: string; item_type?: string }) {
    const client = getApiClient()
    const res = await client.get<PaginatedResponse<PriceBookItem> | PriceBookItem[]>(
      `/quotebuilder/pricebooks/${pricebookId}/items/`,
      withOrgConfig({ params })
    )
    return asArray(res.data)
  },

  async createPricebookItem(pricebookId: string, data: CreatePriceBookItemRequest) {
    const client = getApiClient()
    const res = await client.post<PriceBookItem>(
      `/quotebuilder/pricebooks/${pricebookId}/items/`,
      data,
      withOrgConfig()
    )
    return res.data
  },

  async updatePricebookItem(pricebookId: string, itemId: string, data: Partial<CreatePriceBookItemRequest>) {
    const client = getApiClient()
    const res = await client.patch<PriceBookItem>(
      `/quotebuilder/pricebooks/${pricebookId}/items/${itemId}/`,
      data,
      withOrgConfig()
    )
    return res.data
  },

  async deletePricebookItem(pricebookId: string, itemId: string) {
    const client = getApiClient()
    await client.delete(`/quotebuilder/pricebooks/${pricebookId}/items/${itemId}/`, withOrgConfig())
  },
  // Supplier Quote APIs

  async listSupplierQuotes(params?: { status?: string; supplier?: string }) {
    const client = getApiClient()
    const res = await client.get<PaginatedResponse<SupplierQuoteListItem> | SupplierQuoteListItem[]>(
      `/quotebuilder/supplier_quotes/`,
      withOrgConfig({ params })
    )
    return asArray(res.data)
  },

  async getSupplierQuote(id: string): Promise<SupplierQuoteDetail> {
    const client = getApiClient()
    const res = await client.get<SupplierQuoteDetail>(`/quotebuilder/supplier_quotes/${id}/`, withOrgConfig())
    return res.data
  },

  async uploadSupplierQuote(file: File, supplierId?: string): Promise<SupplierQuoteListItem> {
    const client = getApiClient()
    const formData = new FormData()
    formData.append('file', file)
    if (supplierId) formData.append('supplier_id', supplierId)

    const res = await client.post<SupplierQuoteListItem>(
      `/quotebuilder/supplier_quotes/upload/`,
      formData,
      withOrgConfig({
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    )
    return res.data
  },

  async extractSupplierQuote(id: string, sync: boolean = false): Promise<ExtractionResult> {
    const client = getApiClient()
    const res = await client.post<ExtractionResult>(
      `/quotebuilder/supplier_quotes/${id}/extract/`,
      { sync },
      withOrgConfig()
    )
    return res.data
  },

  async getSupplierQuoteExtractionStatus(id: string): Promise<ExtractionStatus> {
    const client = getApiClient()
    const res = await client.get<ExtractionStatus>(
      `/quotebuilder/supplier_quotes/${id}/extraction_status/`,
      withOrgConfig()
    )
    return res.data
  },

  async applySupplierQuoteLines(
    id: string,
    lineIds: string[],
    pricebookId: string,
    options?: { createNewItems?: boolean; updateExisting?: boolean }
  ): Promise<ApplyLinesResult> {
    const client = getApiClient()
    const res = await client.post<ApplyLinesResult>(
      `/quotebuilder/supplier_quotes/${id}/apply/`,
      {
        line_ids: lineIds,
        pricebook_id: pricebookId,
        create_new_items: options?.createNewItems ?? true,
        update_existing: options?.updateExisting ?? true,
      },
      withOrgConfig()
    )
    return res.data
  },

  async reviewSupplierQuote(id: string): Promise<SupplierQuoteListItem> {
    const client = getApiClient()
    const res = await client.post<SupplierQuoteListItem>(
      `/quotebuilder/supplier_quotes/${id}/review/`,
      {},
      withOrgConfig()
    )
    return res.data
  },

  async deleteSupplierQuote(id: string) {
    const client = getApiClient()
    await client.delete(`/quotebuilder/supplier_quotes/${id}/`, withOrgConfig())
  },
  // Xero Sync APIs

  async getXeroSyncStatus(): Promise<XeroSyncStatus> {
    const client = getApiClient()
    try {
      const res = await client.get<XeroSyncStatus>(`/quotebuilder/pricing/xero/status/`, withOrgConfig())
      return res.data
    } catch (error) {
      // Return default status on error (likely 500 from missing migrations)
      console.warn('Xero sync status API error:', error)
      return {
        is_connected: false,
        last_sync: null,
        items_synced: 0,
        items_pending: 0,
        sync_in_progress: false,
      } as unknown as XeroSyncStatus
    }
  },

  async triggerXeroSync(params?: { full_sync?: boolean; async?: boolean }): Promise<XeroSyncResult> {
    const client = getApiClient()
    const res = await client.post<XeroSyncResult>(
      `/quotebuilder/pricing/xero/sync/`,
      params || {},
      withOrgConfig()
    )
    return res.data
  },
  // Xero Contacts (Suppliers) Sync APIs

  async getXeroContactsStatus(): Promise<XeroContactsStatus> {
    const client = getApiClient()
    const res = await client.get<XeroContactsStatus>(`/quotebuilder/pricing/xero/contacts/status/`, withOrgConfig())
    return res.data
  },

  async triggerXeroContactsSync(): Promise<XeroContactsSyncResult> {
    const client = getApiClient()
    const res = await client.post(
      `/quotebuilder/pricing/xero/contacts/sync/`,
      { full_sync: true },
      withOrgConfig()
    )
    return res.data
  },
  // Historical Pricing APIs

  async analyzeHistoricalPricing(params?: {
    months_back?: number
    min_occurrences?: number
  }): Promise<HistoricalPricingAnalysis> {
    const client = getApiClient()
    const res = await client.get<HistoricalPricingAnalysis>(
      `/quotebuilder/pricing/historical/analyze/`,
      withOrgConfig({ params })
    )
    return res.data
  },

  async generateHistoricalPricebook(params?: {
    name?: string
    months_back?: number
    min_occurrences?: number
  }): Promise<GenerateHistoricalPricebookResult> {
    const client = getApiClient()
    const res = await client.post<GenerateHistoricalPricebookResult>(
      `/quotebuilder/pricing/historical/generate-pricebook/`,
      params || {},
      withOrgConfig()
    )
    return res.data
  },

  async getHistoricalSuggestions(params?: {
    pricebook_id?: string
    min_discrepancy?: number
  }): Promise<HistoricalSuggestionsResult> {
    const client = getApiClient()
    const res = await client.get<HistoricalSuggestionsResult>(
      `/quotebuilder/pricing/historical/suggestions/`,
      withOrgConfig({ params })
    )
    return res.data
  },

  // Pricelist-item attachments (spec sheets / datasheets) + QA checklist.

  async listItemAttachments(pricebookId: string, itemId: string) {
    const client = getApiClient()
    const res = await client.get(
      `/quotebuilder/pricebooks/${pricebookId}/items/${itemId}/attachments/`,
      withOrgConfig(),
    )
    return asArray(res.data)
  },

  async uploadItemAttachment(pricebookId: string, itemId: string, file: File, kind = 'spec') {
    const client = getApiClient()
    const form = new FormData()
    form.append('file', file)
    form.append('kind', kind)
    const res = await client.post(
      `/quotebuilder/pricebooks/${pricebookId}/items/${itemId}/attachments/`,
      form,
      withOrgConfig({ headers: { 'Content-Type': 'multipart/form-data' } }),
    )
    return res.data
  },

  async deleteItemAttachment(pricebookId: string, itemId: string, attachmentId: string) {
    const client = getApiClient()
    await client.delete(
      `/quotebuilder/pricebooks/${pricebookId}/items/${itemId}/attachments/${attachmentId}/`,
      withOrgConfig(),
    )
  },

  async regenerateItemChecklist(pricebookId: string, itemId: string, attachmentId: string) {
    const client = getApiClient()
    const res = await client.post(
      `/quotebuilder/pricebooks/${pricebookId}/items/${itemId}/attachments/${attachmentId}/regenerate_checklist/`,
      {},
      withOrgConfig(),
    )
    return res.data
  },

  async aiEnrichAttachment(pricebookId: string, itemId: string, attachmentId: string) {
    const client = getApiClient()
    const res = await client.post(
      `/quotebuilder/pricebooks/${pricebookId}/items/${itemId}/attachments/${attachmentId}/ai_enrich/`,
      {},
      withOrgConfig(),
    )
    return res.data
  },

  // Material list from quotes → PO issuance for stock ordering.

  async getMaterialTakeoff(jobIds?: number[]): Promise<{
    materials: Array<{ product: string; supplier: string; unit: string; qty: number; est_cost: number; occurrences: number; job_ids: number[] }>
    material_count: number
    total_est_cost: number
  }> {
    const client = getApiClient()
    const params = jobIds && jobIds.length ? { job_ids: jobIds.join(',') } : undefined
    const res = await client.get(`/quotebuilder/pricing/material-takeoff/`, withOrgConfig({ params }))
    return res.data as never
  },

  async issueMaterialPO(body: { job_ids?: number[]; materials?: unknown[]; group_by_supplier?: boolean; job_id?: number }) {
    const client = getApiClient()
    const res = await client.post(`/quotebuilder/pricing/material-takeoff/issue-po/`, body, withOrgConfig())
    return res.data
  },

  // Quote-harvest wizard: list harvestable jobs + run a (scoped) harvest.

  async getHarvestSources(): Promise<{
    jobs: Array<{
      job_id: number; quote_count: number; sheets_with_lines: number;
      lines_total: number; sheets_with_excel: number; last_quote_date: string | null;
    }>;
    job_count: number;
    lines_total: number;
  }> {
    const client = getApiClient()
    const res = await client.get(`/quotebuilder/pricing/harvest-quotes/sources/`, withOrgConfig())
    return res.data as never
  },

  async harvestQuotes(body: {
    dry_run: boolean
    job_ids?: number[]
    parse_excel_fallback?: boolean
    pricebook_id?: string
    min_occurrences?: number
  }): Promise<Record<string, unknown>> {
    const client = getApiClient()
    const res = await client.post(`/quotebuilder/pricing/harvest-quotes/`, body, withOrgConfig())
    return res.data as Record<string, unknown>
  },

  async getItemPriceHistory(search: string, monthsBack?: number): Promise<ItemPriceHistoryResult> {
    const client = getApiClient()
    const res = await client.get<ItemPriceHistoryResult>(
      `/quotebuilder/pricing/historical/item-history/`,
      withOrgConfig({ params: { search, months_back: monthsBack } })
    )
    return res.data
  },
  // Price Suggestions APIs

  async listPriceSuggestions(params?: { status?: string }) {
    const client = getApiClient()
    const res = await client.get<PaginatedResponse<PriceSuggestion> | PriceSuggestion[]>(
      `/quotebuilder/price_suggestions/`,
      withOrgConfig({ params })
    )
    return asArray(res.data)
  },

  async reviewPriceSuggestion(id: string, action: 'approve' | 'reject', reason?: string) {
    const client = getApiClient()
    const res = await client.post(
      `/quotebuilder/pricing/suggestions/${id}/review/`,
      { action, reason },
      withOrgConfig()
    )
    return res.data
  },

  async bulkReviewSuggestions(action: 'approve' | 'reject', suggestionIds: string[], reason?: string) {
    const client = getApiClient()
    const res = await client.post(
      `/quotebuilder/pricing/suggestions/bulk-review/`,
      { action, suggestion_ids: suggestionIds, reason },
      withOrgConfig()
    )
    return res.data
  },
}
